'use client';

// Дашборд «Обзора» (SCHEMA-EDITOR этап 7.2): сверху вниз — строка здоровья, блок
// «Требует внимания» (только когда есть пункты) и карточки сценариев с пульсом за 7 дней.
// Данные — usePersistentData: /overview/pulse (здоровье+сценарии+недостающие доступы) и
// /overview (подписка+баланс для «Требует внимания»). Ниже на странице остаётся журнал.
import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/lib/cabinet-nav';
import { useSlidePanel } from './native-layer-host';
import { useNativeApp } from '@/lib/native-app';
import { useSlideOpen } from '@/lib/native-layers';
import { Tok } from './billing-shared';
import { CreateOptions, createErrText, useCreateGo, useOpenCreateChooser } from './new-scenario-button';
import { Sk } from './skeleton';
import { usePersistentData } from '@/lib/use-persistent-data';
import { KBareButton, KButton } from '@scriptera/ui/kit';

type Outcome = 'ok' | 'error' | 'running' | 'canceled';

export interface ScenarioRow {
  workflowId: string;
  draftId: string | null;
  name: string;
  active: boolean;
  runs7d: number;
  errors7d: number;
  errors24h: number;
  lastRunAt: string | null;
  lastRunOutcome: Outcome | null;
  daily: number[];
  dailyErrors: number[];
  missingCreds: string[];
  nextRunAt: string | null;
}

export interface Pulse {
  ok: boolean;
  state: 'none' | 'unavailable' | 'ok';
  health: { runs24h: number; errors24h: number; runs7d: number };
  scenarios: ScenarioRow[];
  capped: boolean;
  /** Черновиков всего (и без сервера). Optional: в кэше usePersistentData может
   * лежать старый ответ без поля — трактуем как 0. */
  draftsTotal?: number;
}

type ServerState = 'none' | 'ok' | 'frozen' | 'deleted_backup' | 'deleted_clean';

interface Meta {
  ok: boolean;
  state: 'none' | 'provisioning' | 'frozen' | 'ready';
  serverState?: ServerState;
  subscription: { period: string; status: string; periodEnd: string | null } | null;
  balanceRub: number;
}

const LOW_BALANCE_RUB = 50;
const EXPIRY_SOON_MS = 5 * 24 * 60 * 60 * 1000;

// ── иконки (линейные SVG, без эмодзи — требование владельца) ──────────────────────
const IcCheck = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const IcWarn = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.3 3.2 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);
const IcBug = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m8 2 1.9 1.9M14.1 2 16 3.9M18 8a6 6 0 0 0-12 0M6 8v4a6 6 0 0 0 12 0V8M3 13h3M18 13h3M12 20v-8" />
  </svg>
);
const IcPlug = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 22v-5M9 7V2M15 7V2M6 7h12v3a6 6 0 0 1-12 0z" />
  </svg>
);
const IcCard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);
const IcWallet = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5M17 13h.01" />
  </svg>
);

// ── строка здоровья ───────────────────────────────────────────────────────────────
function HealthLine({ pulse, meta }: { pulse: Pulse; meta: Meta | null }) {
  const t = useTranslations('cabinet.overview');
  const failing = pulse.scenarios.filter((s) => s.errors24h > 0);
  const activeCount = pulse.scenarios.filter((s) => s.active).length;
  const frozen = meta?.state === 'frozen';

  let tone: 'ok' | 'bad';
  let title: string;
  let sub: string;
  if (frozen) {
    tone = 'bad';
    title = t('hFrozen');
    sub = t('hFrozenSub');
  } else if (failing.length > 0) {
    tone = 'bad';
    title = t('hBad');
    sub =
      failing.length === 1
        ? t('hBadOne', { name: failing[0]!.name, n: failing[0]!.errors24h })
        : t('hBadMany', { n: failing.length });
  } else {
    tone = 'ok';
    title = t('hOk');
    sub = t('hOkSub', { active: activeCount, runs: pulse.health.runs24h });
  }

  return (
    <div className={`ov-health ${tone}`}>
      <div className="ov-health-ic">{tone === 'ok' ? <IcCheck /> : <IcWarn />}</div>
      <div>
        <div className="ov-health-tt">{title}</div>
        <div className="ov-health-sub">{sub}</div>
      </div>
    </div>
  );
}

// ── баннер состояния сервера (приостановлен / удалён) ───────────────────────────────
// Показываем, когда дашборд иначе молчит (n8n недоступен у frozen, сервера нет у deleted).
// Кнопка ведёт в «Настройки» → модалка оплаты (тот же flow, никаких новых платёжных путей).
function ServerBanner({ state }: { state: 'frozen' | 'deleted_backup' | 'deleted_clean' }) {
  const t = useTranslations('cabinet.overview');
  // Мобильное приложение: «продли/оплати» — предложение покупки, в приложении запрещено
  // (CLAUDE.md п.7) — состояние показываем нейтральным текстом без кнопки.
  const native = useNativeApp();
  const k = {
    frozen: { tt: 'srvFrozenTt', sub: 'srvFrozenSub', btn: 'srvFrozenBtn' },
    deleted_backup: { tt: 'srvDelBackupTt', sub: 'srvDelBackupSub', btn: 'srvDelBackupBtn' },
    deleted_clean: { tt: 'srvDelCleanTt', sub: 'srvDelCleanSub', btn: 'srvDelCleanBtn' },
  }[state];
  return (
    <div className="ov-health bad">
      <div className="ov-health-ic"><IcWarn /></div>
      <div style={{ flex: 1 }}>
        <div className="ov-health-tt">{t(k.tt)}</div>
        <div className="ov-health-sub">{t(native ? `${k.sub}App` : k.sub)}</div>
      </div>
      {!native && (
        <Link href="/app/settings" className="btn sm primary" style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          {t(k.btn)}
        </Link>
      )}
    </div>
  );
}

// ── сервер в пути (готовится/переезжает) ────────────────────────────────────────────
// Честная строка вместо пустого «Обзора» (PRICING-PLANS-PLAN этап 4): состояние
// временное — говорим это прямо и предлагаем собирать сценарий уже сейчас
// (черновики сервера не требуют).
function PreparingLine() {
  const t = useTranslations('cabinet.overview');
  return (
    <div className="ov-health ok">
      <div className="ov-health-ic">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      </div>
      <div>
        <div className="ov-health-tt">{t('preparingTt')}</div>
        <div className="ov-health-sub">{t('preparingSub')}</div>
      </div>
    </div>
  );
}

// Сервер выдан, но не отвечает (рестарт, сеть) — честная плашка вместо пустой
// страницы (Ф26): раньше «Обзор» в этом состоянии молчал, клиент упирался в пустоту.
function UnavailableLine({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations('cabinet.overview');
  return (
    <div className="ov-health bad">
      <div className="ov-health-ic"><IcWarn /></div>
      <div>
        <div className="ov-health-tt">{t('srvUnreachTt')}</div>
        <div className="ov-health-sub">{t('srvUnreachSub')}</div>
      </div>
      <KButton size="sm" onClick={onRetry} style={{ marginLeft: 'auto', alignSelf: 'center' }}>
        {t('retry')}
      </KButton>
    </div>
  );
}

// Плашка акции первых часов переехала в сквозной верх кабинета (BillingTopChrome в
// billing.tsx, заказ владельца 2026-07-17) — на «Обзоре» её больше нет.

// ── блок «Требует внимания» ─────────────────────────────────────────────────────────
interface AttnItem {
  key: string;
  tone: 'err' | 'warn';
  icon: React.ReactNode;
  title: string;
  sub: string;
  /** Кнопки нет у пунктов без действия (режим приложения: покупку предлагать нельзя). */
  btn?: string;
  href?: string;
}

function buildAttention(
  pulse: Pulse,
  meta: Meta | null,
  t: ReturnType<typeof useTranslations>,
  locale: string,
  // Режим приложения: пункты-предложения оплаты (продлить/пополнить/перейти на тариф)
  // не показываем совсем, у лимита шагов — только факт без апселла (CLAUDE.md п.7).
  native: boolean,
): AttnItem[] {
  const items: AttnItem[] = [];
  const scenarioHref = (s: ScenarioRow) => (s.draftId ? `/app/bots/${s.draftId}` : '/app/bots');

  // (а) сценарии с ошибками за сутки → «Разобраться» (ссылка в чат сценария)
  for (const s of pulse.scenarios.filter((x) => x.errors24h > 0)) {
    items.push({
      key: `err-${s.workflowId}`,
      tone: 'err',
      icon: <IcBug />,
      title: t('attnErr', { name: s.name }),
      sub: t('attnErrSub', { n: s.errors24h }),
      btn: t('attnErrBtn'),
      href: scenarioHref(s),
    });
  }
  // (б) включённый сценарий потерял доступ → «Подключить»
  for (const s of pulse.scenarios.filter((x) => x.missingCreds.length > 0)) {
    items.push({
      key: `cred-${s.workflowId}`,
      tone: 'warn',
      icon: <IcPlug />,
      title: t('attnCred', { name: s.name }),
      sub: t('attnCredSub'),
      btn: t('attnCredBtn'),
      href: '/app/connections',
    });
  }
  // (в) сервер заморожен / тариф кончается → «Продлить» (в приложении не показываем)
  if (native) return items;
  if (meta?.state === 'frozen') {
    items.push({
      key: 'frozen',
      tone: 'warn',
      icon: <IcCard />,
      title: t('attnFrozen'),
      sub: t('attnFrozenSub'),
      btn: t('attnRenewBtn'),
      href: '/app/settings',
    });
  } else if (meta?.subscription?.periodEnd) {
    const end = Date.parse(meta.subscription.periodEnd);
    if (!Number.isNaN(end) && end - Date.now() < EXPIRY_SOON_MS) {
      const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(new Date(end));
      items.push({
        key: 'expiring',
        tone: 'warn',
        icon: <IcCard />,
        title: t('attnExpiring', { date }),
        sub: t('attnExpiringSub'),
        btn: t('attnRenewBtn'),
        href: '/app/settings',
      });
    }
  }
  // (г) ИИ-баланс на исходе → «Пополнить»
  if (meta && meta.balanceRub < LOW_BALANCE_RUB) {
    items.push({
      key: 'balance',
      tone: 'warn',
      icon: <IcWallet />,
      title: t('attnBalance'),
      sub: t('attnBalanceSub', { n: Math.max(0, Math.round(meta.balanceRub)) }),
      btn: t('attnTopupBtn'),
      href: '/app/settings',
    });
  }
  return items;
}

function Attention({ items }: { items: AttnItem[] }) {
  const t = useTranslations('cabinet.overview');
  if (items.length === 0) return null;
  return (
    <>
      <div className="h2">{t('attnTitle')}</div>
      <div className="ov-attn">
        {items.map((it) => (
          <div className={`ov-attn-row ${it.tone}`} key={it.key}>
            <div className="ov-attn-ic">{it.icon}</div>
            <div className="ov-attn-tx">
              <b>{it.title}</b>
              <div>{it.sub}</div>
            </div>
            {it.btn && it.href ? (
              <Link href={it.href} className={`btn sm${it.tone === 'err' ? ' primary' : ''}`}>
                {it.btn}
              </Link>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
}

// ── карточки сценариев с пульсом ────────────────────────────────────────────────────
function ScenarioCards({ scenarios }: { scenarios: ScenarioRow[] }) {
  const t = useTranslations('cabinet.overview');
  const locale = useLocale();
  // Открытие сценария с «Обзора» — родным боковым выездом (в приложении); ссылка на раздел
  // (без draftId) остаётся обычной навигацией.
  const slideOpen = useSlideOpen();
  const openScn = (href: string, isScn: boolean) => (e: React.MouseEvent) => {
    if (!isScn || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (slideOpen(href)) e.preventDefault();
  };
  if (scenarios.length === 0) return null;

  // Подпись дня для tooltip столбика: индексы daily — скользящие сутки (0 = последние 24 ч).
  const dayLabel = (idx: number) =>
    idx === 0 ? t('scnBarToday') : idx === 1 ? t('scnBarYesterday') : t('scnBarDaysAgo', { d: idx });
  const barTip = (idx: number, v: number, e: number) =>
    e > 0
      ? t('scnBarTipErr', { when: dayLabel(idx), n: v, e })
      : t('scnBarTip', { when: dayLabel(idx), n: v });

  const fmtWhen = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(
          new Date(iso),
        )
      : '';

  // Карточки: сначала с ошибками, потом активные, потом по числу запусков.
  const sorted = [...scenarios].sort(
    (a, b) =>
      Number(b.errors24h > 0) - Number(a.errors24h > 0) ||
      Number(b.active) - Number(a.active) ||
      b.runs7d - a.runs7d,
  );

  return (
    <>
      <div className="h2">{t('scnTitle')}</div>
      <div className="ov-scn-grid">
        {sorted.map((s) => {
          const max = Math.max(1, ...s.daily);
          const hasError = s.errors24h > 0;
          const href = s.draftId ? `/app/bots/${s.draftId}` : '/app/bots';
          return (
            <Link href={href} className={`ov-scn${hasError ? ' err' : ''}`} key={s.workflowId} onClick={openScn(href, !!s.draftId)}>
              <div className="ov-scn-top">
                <div className="ov-scn-name">{s.name}</div>
                {/* Точка вместо чипа «включён/выключен» (заказ владельца 2026-07-10):
                    зелёная — работает, серая — на паузе, жёлтая — были ошибки за сутки.
                    Подпись — title на десктопе (на мобиле хватает цвета). */}
                <span
                  className={`ov-scn-st ${hasError ? 'warn' : s.active ? 'ok' : 'mut'}`}
                  title={
                    hasError
                      ? `${s.active ? t('scnOn') : t('scnOff')} · ${t('attnErrSub', { n: s.errors24h })}`
                      : s.active
                        ? t('scnOn')
                        : t('scnOff')
                  }
                />
              </div>
              {/* пульс: слева старое → справа сегодня. Столбик дня с ошибкой — красный;
                  на hover — tooltip с днём и числами (мобиле хватает цифр в подвале). */}
              <div className="ov-bars" aria-hidden>
                {[...s.daily]
                  .map((v, i) => ({ v, e: s.dailyErrors[i] ?? 0, idx: i }))
                  .reverse()
                  .map(({ v, e, idx }, i) => (
                    <i
                      key={i}
                      className={v === 0 ? 'z' : e > 0 ? 'e' : ''}
                      data-tip={barTip(idx, v, e)}
                      style={{ height: v === 0 ? 3 : Math.round(6 + (v / max) * 30) }}
                    />
                  ))}
              </div>
              <div className="ov-scn-foot">
                <span className="ov-scn-last">
                  {s.lastRunOutcome && (
                    <span className={`ov-dot ${s.lastRunOutcome === 'error' ? 'err' : 'ok'}`} />
                  )}
                  {s.lastRunAt ? t('scnLastAt', { when: fmtWhen(s.lastRunAt) }) : t('scnLastNever')}
                </span>
                <span className="mono">{t('scnWeek', { n: s.runs7d })}</span>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}

// ── расходы на ИИ (общая сумма на аккаунт, свой хук/скелетон) ───────────────────────
interface Spend {
  ok: boolean;
  available: boolean;
  spend7d: number;
  spend30d: number;
  daily?: number[]; // ₽ по суткам за 30 дней, старое → сегодня (кэш старого формата — без поля)
}

// Мини-тренд стат-плитки: тихая линия по дням, точка «сегодня» — акцент (DESIGN-REFRESH этап 6).
function Spark({ daily }: { daily: number[] }) {
  if (daily.length < 2) return null;
  const W = 72;
  const H = 26;
  const PAD = 3; // чтобы точка «сегодня» и линия не резались краями viewBox
  const max = Math.max(1, ...daily);
  const pts = daily.map((v, i) => ({
    x: PAD + (i / (daily.length - 1)) * (W - PAD * 2),
    y: H - PAD - (v / max) * (H - PAD * 2),
  }));
  const last = pts[pts.length - 1]!;
  return (
    <svg className="ov-spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden>
      <polyline
        points={pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last.x} cy={last.y} r={3} />
    </svg>
  );
}

function SpendBlock() {
  const t = useTranslations('cabinet.overview');
  const panel = useSlidePanel(); // расходы: родной выезд в приложении, drawer в вебе
  // Расходы на ИИ — в токенах (1 ₽ = 1 токен), рублями оплачиваются только тарифы (владелец 2026-07-14).
  const { data } = usePersistentData<Spend>('cabinet:overview:spend', '/api/cabinet/overview/spend', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
  });
  // Шлюз недоступен или трат ещё не было — блок не показываем (не шумим нулями).
  if (!data || !data.available || (data.spend7d <= 0 && data.spend30d <= 0)) return null;
  const daily = data.daily ?? [];
  // Плитка — кнопка: открывает шторку со всеми операциями по расходам (заказ 2026-07-10).
  // Список трат — вертикальная шторка ВЕЗДЕ (как история ИИ; направление одно на мобильный
  // сайт и приложение, владелец 2026-07-21).
  const openOps = () => panel.open({ title: t('spendTitle'), sheet: true, body: <SpendOpsSheet /> });
  return (
    <>
      <div className="h2">{t('spendTitle')}</div>
      <div className="ov-spend">
        <KBareButton className="ov-spend-item" onClick={openOps}>
          <div className="ov-spend-tx">
            <span className="ov-spend-n"><Tok n={data.spend7d} size={13} /></span>
            <span>{t('spendWeek')}</span>
          </div>
          <Spark daily={daily.slice(-7)} />
        </KBareButton>
        <KBareButton className="ov-spend-item" onClick={openOps}>
          <div className="ov-spend-tx">
            <span className="ov-spend-n"><Tok n={data.spend30d} size={13} /></span>
            <span>{t('spendMonth')}</span>
          </div>
          <Spark daily={daily} />
        </KBareButton>
      </div>
    </>
  );
}

// ── шторка «Расходы на ИИ»: все операции-списания по аккаунту ───────────────────────
interface SpendOp {
  id: string;
  ts: string;
  modelName: string;
  chargedRub: number;
}

/** Журнал списаний. from/to (ISO, опц.) — период для шторки «Токены» (spend-report.tsx). */
export function SpendOpsSheet({ from, to }: { from?: string; to?: string } = {}) {
  const t = useTranslations('cabinet.overview');
  const locale = useLocale();
  // Списания — в токенах, суммы бывают дробными (доли токена).
  const [rows, setRows] = useState<SpendOp[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  // Смещение по СЫРЫМ строкам шлюза (роут отдаёт nextOffset): фильтр «только списания»
  // съедает часть строк, и rows.length как offset топтался бы на месте.
  const [offset, setOffset] = useState(0);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  const load = async (off: number) => {
    const range = `${from ? `&from=${encodeURIComponent(from)}` : ''}${to ? `&to=${encodeURIComponent(to)}` : ''}`;
    const r = await fetch(`/api/cabinet/overview/spend/ops?offset=${off}${range}`, { cache: 'no-store' });
    const j = (await r.json()) as { ok: boolean; rows: SpendOp[]; hasMore: boolean; nextOffset: number };
    if (!r.ok || !j?.ok) throw new Error('load failed');
    return j;
  };

  useEffect(() => {
    let alive = true;
    load(0)
      .then((j) => {
        if (!alive) return;
        setRows(j.rows);
        setHasMore(j.hasMore);
        setOffset(j.nextOffset);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load пересоздаётся каждый рендер, входы — nonce и период
  }, [nonce, from, to]);

  const more = async () => {
    if (busy || !rows) return;
    setBusy(true);
    try {
      const j = await load(offset);
      const seen = new Set(rows.map((x) => x.id));
      setRows([...rows, ...j.rows.filter((x) => !seen.has(x.id))]);
      setHasMore(j.hasMore);
      setOffset(j.nextOffset);
    } catch {
      /* сеть мигнула — кнопка остаётся */
    } finally {
      setBusy(false);
    }
  };

  const fmtWhen = (iso: string) =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(
      new Date(iso),
    );

  if (failed) {
    return (
      <div className="runs-empty">
        {t('loadError')}{' '}
        <KButton
          size="sm"
          onClick={() => {
            setFailed(false);
            setRows(null);
            setNonce((n) => n + 1);
          }}
        >
          {t('retry')}
        </KButton>
      </div>
    );
  }
  if (rows === null) {
    return (
      <div className="runs sk-hold" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div className="runs-row" key={i}>
            <div className="runs-main">
              <Sk w={150} h={15} />
              <Sk w={90} h={12} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (rows.length === 0) return <div className="runs-empty">{t('opsEmpty')}</div>;

  return (
    <div className="runs-wrap">
      <div className="runs">
        {rows.map((r) => (
          <div className="runs-row static" key={r.id}>
            <div className="runs-main">
              <div className="runs-top">
                <b>{r.modelName}</b>
              </div>
              <div className="runs-sub">{fmtWhen(r.ts)}</div>
            </div>
            <span className="ops-sum mono">−<Tok n={r.chargedRub} price size={11} /></span>
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="runs-more">
          <KButton size="sm" onClick={more} disabled={busy}>
            {busy ? t('jrMoreBusy') : t('jrMore')}
          </KButton>
        </div>
      )}
    </div>
  );
}

// ── ближайшие запуски по расписанию ─────────────────────────────────────────────────
function UpcomingRuns({ scenarios }: { scenarios: ScenarioRow[] }) {
  const t = useTranslations('cabinet.overview');
  const locale = useLocale();
  const slideOpen = useSlideOpen();
  const openScn = (href: string, isScn: boolean) => (e: React.MouseEvent) => {
    if (!isScn || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (slideOpen(href)) e.preventDefault();
  };
  const upcoming = scenarios
    .filter((s) => s.nextRunAt)
    .map((s) => ({ s, at: Date.parse(s.nextRunAt!) }))
    .filter((x) => !Number.isNaN(x.at))
    .sort((a, b) => a.at - b.at)
    .slice(0, 5);
  if (upcoming.length === 0) return null;

  const fmt = (at: number) =>
    new Intl.DateTimeFormat(locale, { weekday: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(at));

  return (
    <>
      <div className="h2">{t('upcomingTitle')}</div>
      <div className="ov-up">
        {upcoming.map(({ s, at }) => {
          const href = s.draftId ? `/app/bots/${s.draftId}` : '/app/bots';
          return (
            <Link href={href} className="ov-up-row" key={s.workflowId} onClick={openScn(href, !!s.draftId)}>
              <span className="ov-up-name">{s.name}</span>
              <span className="ov-up-at mono">{fmt(at)}</span>
            </Link>
          );
        })}
      </div>
    </>
  );
}

// ── пустое состояние: нет сценариев → онбординг ─────────────────────────────────────
// Мотивация + плашка четырёх путей создания ПРЯМО на странице (владелец 2026-07-13);
// плашка — общий компонент CreateOptions (та же в модалке «Новый сценарий»).
// Чат/схема создают черновик сразу, шаблоны/импорт открывают модалку на своём экране.
function EmptyState() {
  const t = useTranslations('cabinet.overview');
  const ta = useTranslations('cabinet.automations');
  const openChooser = useOpenCreateChooser();
  const { go, busy, err } = useCreateGo();
  return (
    <div className="ov-empty">
      <div className="ov-empty-tt">{t('emptyTitle')}</div>
      <div className="ov-empty-sub">{t('emptySub')}</div>
      {err && <div className="keybanner">{createErrText(ta, err)}</div>}
      <div className="ov-empty-create">
        <CreateOptions
          busy={busy}
          onPick={(a) => {
            if (a === 'dialog') void go({}, 'dialog');
            else if (a === 'schema') void go({}, 'schema');
            else openChooser(a);
          }}
        />
      </div>
    </div>
  );
}

/** ПУЛЬС СЦЕНАРИЕВ — один ключ и один запрос на весь кабинет (ряды запусков по дням,
 *  «за неделю», сбои за сутки по каждому боту). Свой fetch по месту не заводить: «Обзор»,
 *  «Аналитика» и страница бота обязаны показывать одни и те же цифры. */
export function usePulse() {
  return usePersistentData<Pulse>('cabinet:overview:pulse', '/api/cabinet/overview/pulse', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
  });
}

export function OverviewDashboard() {
  const t = useTranslations('cabinet.overview');
  const locale = useLocale();
  const native = useNativeApp();
  const { data: pulse, loading, error, reload } = usePulse();
  const { data: meta } = usePersistentData<Meta>('cabinet:overview', '/api/cabinet/overview', {
    accept: (j) => !!(j as { ok?: boolean })?.ok,
  });

  if (loading && !error) {
    // Скелетон повторяет каркас дашборда (строка здоровья + карточки), не дыру (этап 6).
    return (
      <>
        <div className="ov-health sk-hold">
          <Sk w={44} h={44} />
          <div style={{ flex: 1 }}>
            <Sk w={160} h={20} />
            <div style={{ height: 8 }} />
            <Sk w={240} h={14} />
          </div>
        </div>
        <div className="ov-scn-grid sk-hold" aria-hidden>
          {[0, 1].map((i) => (
            <div className="ov-scn" key={i}>
              <Sk w={150} h={18} />
              <Sk w="100%" h={38} r="xs" />
              <Sk w={190} h={13} />
            </div>
          ))}
        </div>
      </>
    );
  }

  // Данные не пришли или сервера ещё нет — дашборд молчит (журнал ниже сам объяснит статус,
  // онбординг пустого состояния — этап 7.3). Ошибку загрузки показываем ненавязчиво.
  if (pulse === null) {
    return (
      <div className="ov-health">
        <div className="ov-health-sub">{t('loadError')}</div>
        <KButton size="sm" onClick={reload} style={{ marginLeft: 'auto' }}>
          {t('retry')}
        </KButton>
      </div>
    );
  }
  // Баннер сервера (приостановлен/удалён) — рендерим ДО гейта готовности: у frozen/deleted
  // pulse.state ≠ ok, иначе дашборд молчал бы. При рабочем сервере serverState='ok' → null.
  const srv = meta?.serverState;
  const srvBanner =
    srv === 'frozen' || srv === 'deleted_backup' || srv === 'deleted_clean' ? (
      <ServerBanner state={srv} />
    ) : null;
  // Акция первых часов — до всех гейтов: у свежего free-юзера ещё нет сценариев.

  // Сервер в пути (нового юзера или после переезда на free): вместо пустой страницы —
  // честная строка «готовим сервер» + онбординг создания (черновики работают без сервера).
  // Гейты: без meta не гадаем; frozen/deleted объясняет ServerBanner; state='ready' с
  // недоступным сервером — плашка UnavailableLine ниже (Ф26).
  const preparing =
    !srvBanner && meta != null && (meta.state === 'none' || meta.state === 'provisioning');

  // Онбординг «создай первый сценарий» — ТОЛЬКО совсем новому юзеру: ни живых сценариев,
  // ни черновиков (владелец 2026-07-17). Есть хоть один черновик → обычные виджеты.
  const nothingCreated = (pulse.draftsTotal ?? 0) === 0;

  if (pulse.state !== 'ok')
    return (
      <>
        {srvBanner}
        {preparing && (
          <>
            <PreparingLine />
            {nothingCreated && <EmptyState />}
          </>
        )}
        {/* Ф26: сервер выдан и должен работать, но не отвечает — не молчать. */}
        {!srvBanner && !preparing && pulse.state === 'unavailable' && (
          <UnavailableLine onRetry={reload} />
        )}
      </>
    );

  // Нет ни сценариев, ни черновиков — онбординг вместо дашборда (журнал ниже сам объяснит пустоту).
  if (pulse.scenarios.length === 0 && nothingCreated)
    return (
      <>
        {srvBanner}
        <EmptyState />
      </>
    );

  const attn = buildAttention(pulse, meta, t, locale, native);

  return (
    <>
      {srvBanner}
      <HealthLine pulse={pulse} meta={meta} />
      <Attention items={attn} />
      <ScenarioCards scenarios={pulse.scenarios} />
      <SpendBlock />
      <UpcomingRuns scenarios={pulse.scenarios} />
    </>
  );
}
